import type {
  AtlCheckDetailPublic,
  AtlComponentChangePublic,
  AtlDefectDetailPublic,
  AtlFluidUpliftDetailPublic,
  AtlPageDetailPublic,
  AtlSectorDetailPublic,
  CheckOperation,
  ComponentChangeOperation,
  DefectOperation,
  FluidUpliftOperation,
  SectorOperation,
  UpdateAtlPage,
  UpdateCheck,
  UpdateComponentChange,
  UpdateDefect,
  UpdateFluidUplift,
  UpdateSector,
} from "@common/client"
import { decimal, given, only } from "./optimistic"

const PAGE_FIELDS: ReadonlySet<string> = new Set<keyof UpdateAtlPage>([
  "log_number",
  "operator",
  "aircraft_registration",
  "aircraft_type",
  "commander",
  "first_officer",
  "purser",
  "cabin_crew",
  "form_reference",
  "maintenance_status_reference",
  "current_crs_reference",
  "flagged_fields",
  "sectors",
  "defects",
  "component_changes",
  "checks",
  "fluid_uplifts",
])

const SECTOR_FIELDS: ReadonlySet<string> = new Set<keyof UpdateSector>([
  "operation",
  "id",
  "flight_date",
  "flight_number",
  "from_station",
  "to_station",
  "block_off",
  "block_on",
  "flight_off",
  "flight_on",
  "landings",
  "flight_cycles",
  "hours_since_new",
  "cycles_since_new",
])

const DEFECT_FIELDS: ReadonlySet<string> = new Set<keyof UpdateDefect>([
  "operation",
  "id",
  "description",
  "reported_by",
  "reported_date",
  "reported_time",
  "ata_chapter",
  "item_number",
  "complaint_number",
  "fault_code",
  "corrective_action",
  "accomplished_iaw",
  "work_order_number",
  "maintenance_completed",
])

const CHANGE_FIELDS: ReadonlySet<string> = new Set<keyof UpdateComponentChange>(
  [
    "operation",
    "id",
    "component_name",
    "part_number_off",
    "serial_number_off",
    "tag_number_off",
    "part_number_on",
    "serial_number_on",
    "tag_number_on",
    "form1_reference",
    "removal_note",
  ],
)

const CHECK_FIELDS: ReadonlySet<string> = new Set<keyof UpdateCheck>([
  "operation",
  "id",
  "check_name",
  "station_code",
])

const UPLIFT_FIELDS: ReadonlySet<string> = new Set<keyof UpdateFluidUplift>([
  "operation",
  "id",
  "fluid_type",
  "position",
  "quantity",
  "unit",
])

function predictable(
  fields: ReadonlySet<string>,
  operations:
    | SectorOperation[]
    | DefectOperation[]
    | ComponentChangeOperation[]
    | CheckOperation[]
    | FluidUpliftOperation[]
    | undefined,
): boolean {
  return (operations ?? []).every((operation) => {
    switch (operation.operation) {
      case "update":
        return only(fields, operation)
      case "remove":
        return true
      case "create":
        return false
    }
  })
}

export function isPredictableUpdate(body: UpdateAtlPage): boolean {
  return (
    only(PAGE_FIELDS, body) &&
    predictable(SECTOR_FIELDS, body.sectors) &&
    predictable(DEFECT_FIELDS, body.defects) &&
    predictable(CHANGE_FIELDS, body.component_changes) &&
    predictable(CHECK_FIELDS, body.checks) &&
    predictable(UPLIFT_FIELDS, body.fluid_uplifts)
  )
}

function rowsWith<
  R extends { id: string },
  O extends { operation: "create" | "update" | "remove" },
>(rows: R[], operations: O[] | undefined, apply: (row: R, op: O) => R): R[] {
  return (operations ?? []).reduce((current, operation) => {
    switch (operation.operation) {
      case "update":
        return current.map((row) =>
          "id" in operation && row.id === operation.id
            ? apply(row, operation)
            : row,
        )
      case "remove":
        return current.filter(
          (row) => !("id" in operation) || row.id !== operation.id,
        )
      case "create":
        return current
    }
  }, rows)
}

function sectorWith(
  sector: AtlSectorDetailPublic,
  operation: SectorOperation,
): AtlSectorDetailPublic {
  if (operation.operation !== "update") return sector
  return {
    ...sector,
    flight_date: given(operation.flight_date, sector.flight_date),
    flight_number: given(operation.flight_number, sector.flight_number),
    from_station: given(operation.from_station, sector.from_station),
    to_station: given(operation.to_station, sector.to_station),
    block_off: given(operation.block_off, sector.block_off),
    block_on: given(operation.block_on, sector.block_on),
    flight_off: given(operation.flight_off, sector.flight_off),
    flight_on: given(operation.flight_on, sector.flight_on),
    landings: given(operation.landings, sector.landings),
    flight_cycles: given(operation.flight_cycles, sector.flight_cycles),
    hours_since_new: decimal(operation.hours_since_new, sector.hours_since_new),
    cycles_since_new: decimal(
      operation.cycles_since_new,
      sector.cycles_since_new,
    ),
  }
}

function defectWith(
  defect: AtlDefectDetailPublic,
  operation: DefectOperation,
): AtlDefectDetailPublic {
  if (operation.operation !== "update") return defect
  return {
    ...defect,
    description: given(operation.description, defect.description),
    reported_by: given(operation.reported_by, defect.reported_by),
    reported_date: given(operation.reported_date, defect.reported_date),
    reported_time: given(operation.reported_time, defect.reported_time),
    ata_chapter: given(operation.ata_chapter, defect.ata_chapter),
    item_number: given(operation.item_number, defect.item_number),
    complaint_number: given(
      operation.complaint_number,
      defect.complaint_number,
    ),
    fault_code: given(operation.fault_code, defect.fault_code),
    corrective_action: given(
      operation.corrective_action,
      defect.corrective_action,
    ),
    accomplished_iaw: given(
      operation.accomplished_iaw,
      defect.accomplished_iaw,
    ),
    work_order_number: given(
      operation.work_order_number,
      defect.work_order_number,
    ),
    maintenance_completed: given(
      operation.maintenance_completed,
      defect.maintenance_completed,
    ),
  }
}

function changeWith(
  change: AtlComponentChangePublic,
  operation: ComponentChangeOperation,
): AtlComponentChangePublic {
  if (operation.operation !== "update") return change
  return {
    ...change,
    component_name: given(operation.component_name, change.component_name),
    part_number_off: given(operation.part_number_off, change.part_number_off),
    serial_number_off: given(
      operation.serial_number_off,
      change.serial_number_off,
    ),
    tag_number_off: given(operation.tag_number_off, change.tag_number_off),
    part_number_on: given(operation.part_number_on, change.part_number_on),
    serial_number_on: given(
      operation.serial_number_on,
      change.serial_number_on,
    ),
    tag_number_on: given(operation.tag_number_on, change.tag_number_on),
    form1_reference: given(operation.form1_reference, change.form1_reference),
    removal_note: given(operation.removal_note, change.removal_note),
  }
}

function checkWith(
  check: AtlCheckDetailPublic,
  operation: CheckOperation,
): AtlCheckDetailPublic {
  if (operation.operation !== "update") return check
  return {
    ...check,
    check_name: given(operation.check_name, check.check_name),
    station_code: given(operation.station_code, check.station_code),
  }
}

function upliftWith(
  uplift: AtlFluidUpliftDetailPublic,
  operation: FluidUpliftOperation,
): AtlFluidUpliftDetailPublic {
  if (operation.operation !== "update") return uplift
  return {
    ...uplift,
    fluid_type: given(operation.fluid_type, uplift.fluid_type),
    position: given(operation.position, uplift.position),
    quantity: decimal(operation.quantity, uplift.quantity) ?? uplift.quantity,
    unit: given(operation.unit, uplift.unit),
  }
}

export function atlPageWithUpdate(
  page: AtlPageDetailPublic,
  body: UpdateAtlPage,
): AtlPageDetailPublic {
  return {
    ...page,
    log_number: given(body.log_number, page.log_number),
    operator: given(body.operator, page.operator),
    aircraft_registration: given(
      body.aircraft_registration,
      page.aircraft_registration,
    ),
    aircraft_type: given(body.aircraft_type, page.aircraft_type),
    commander: given(body.commander, page.commander),
    first_officer: given(body.first_officer, page.first_officer),
    purser: given(body.purser, page.purser),
    cabin_crew: given(body.cabin_crew, page.cabin_crew),
    form_reference: given(body.form_reference, page.form_reference),
    maintenance_status_reference: given(
      body.maintenance_status_reference,
      page.maintenance_status_reference,
    ),
    current_crs_reference: given(
      body.current_crs_reference,
      page.current_crs_reference,
    ),
    flagged_fields: given(body.flagged_fields, page.flagged_fields),
    sectors: rowsWith(page.sectors, body.sectors, sectorWith).map((sector) => ({
      ...sector,
      fluid_uplifts: rowsWith(
        sector.fluid_uplifts,
        body.fluid_uplifts,
        upliftWith,
      ),
    })),
    defects: rowsWith(page.defects, body.defects, defectWith),
    component_changes: rowsWith(
      page.component_changes,
      body.component_changes,
      changeWith,
    ),
    checks: rowsWith(page.checks, body.checks, checkWith),
  }
}
